import { type Meta, createRedemptionUsingBankCurrency } from '@koala/sdk/v4';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BankedCurrencyModal } from '@/components/account/rewards/back-currency-modal/banked-currency-modal';
import { useBankCurrencyRedemption } from '@/components/account/rewards/redeem-in-store-modal/hooks';
import { ScanCode } from '@/components/account/rewards/scan-code';
import { REWARD_TYPE } from '@/constants/loyalty';
import meActions from '@/redux/me/actions';
import { meSliceSelector } from '@/redux/me/reducer';
import { createHttpClient } from '@/services/client';
import { getOrigin } from '@/utils';

interface Props {
  initialAmount: number;
  onClose: () => void;
}

export const RedeemInStoreModal = ({ onClose, initialAmount }: Props) => {
  const dispatch = useDispatch();
  const { data: meData, activeRedemption } = useSelector(meSliceSelector);

  const {
    isLoading,
    isSuccess,
    mutate: createRedemption,
  } = useMutation({
    mutationFn: ({ amount, client }: { amount: number; client: Meta }) =>
      createRedemptionUsingBankCurrency(amount, client),
    onError: (error) => {
      dispatch(meActions.createRedemptionFailure(error, REWARD_TYPE.REWARD));
    },
    onSuccess: (redemption) => {
      dispatch(meActions.createRedemptionSuccess(redemption, REWARD_TYPE.REWARD));
    },
  });

  const bankedCurrencyRedemption = useBankCurrencyRedemption();

  useEffect(() => {
    if (bankedCurrencyRedemption && !activeRedemption) {
      dispatch(meActions.createRedemptionSuccess(bankedCurrencyRedemption, REWARD_TYPE.REWARD));
    }
  }, [bankedCurrencyRedemption, activeRedemption]);

  useEffect(() => {
    return () => {
      dispatch(meActions.clearActiveRedemption());
    };
  }, []);

  return isSuccess || activeRedemption ? (
    <ScanCode type={REWARD_TYPE.REWARD} memberId={meData?.external_user_fk} onError={onClose} />
  ) : (
    <BankedCurrencyModal
      onSubmit={(amount: number) =>
        // createRedemption is mutate(...)
        createRedemption({
          amount,
          client: {
            client: createHttpClient({
              // we should always be in a window when dealing with user sessions
              origin: getOrigin(window.location.host),
            }),
          },
        })
      }
      initialAmount={initialAmount}
      rewardActionType={'redeem'}
      isLoading={isLoading}
    />
  );
};
